/**
 * ZITELOU - Profile Page
 * Page de profil administrateur avec modification des informations personnelles
 */

import React, { useState } from 'react';
import {
  UserCircleIcon,
  EnvelopeIcon,
  PhoneIcon,
  LockClosedIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';
import { Card, Input, Button } from '../components/Common';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';
import { isValidEmail, isValidPhone, getInitials } from '../utils';
import api from '../services/api';

const Profile = () => {
  const { user, updateUser } = useAuth();
  const [activeTab, setActiveTab] = useState('info'); // 'info' or 'password'

  // Profile form state
  const [profileForm, setProfileForm] = useState({
    firstname: user?.firstname || '',
    lastname: user?.lastname || '',
    email: user?.email || '',
    phone: user?.phone || '',
  });

  const [profileErrors, setProfileErrors] = useState({});
  const [profileLoading, setProfileLoading] = useState(false);

  // Password form state
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const [passwordErrors, setPasswordErrors] = useState({});
  const [passwordLoading, setPasswordLoading] = useState(false);

  // Handle profile form change
  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    setProfileForm((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error when user types
    if (profileErrors[name]) {
      setProfileErrors((prev) => ({
        ...prev,
        [name]: null,
      }));
    }
  };

  // Handle password form change
  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordForm((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error when user types
    if (passwordErrors[name]) {
      setPasswordErrors((prev) => ({
        ...prev,
        [name]: null,
      }));
    }
  };

  // Validate profile form
  const validateProfileForm = () => {
    const errors = {};

    if (!profileForm.firstname.trim()) {
      errors.firstname = 'Le prénom est requis';
    }

    if (!profileForm.lastname.trim()) {
      errors.lastname = 'Le nom est requis';
    }

    if (!profileForm.email.trim()) {
      errors.email = "L'email est requis";
    } else if (!isValidEmail(profileForm.email)) {
      errors.email = 'Email invalide';
    }

    if (profileForm.phone && !isValidPhone(profileForm.phone)) {
      errors.phone = 'Numéro de téléphone invalide';
    }

    return errors;
  };

  // Validate password form
  const validatePasswordForm = () => {
    const errors = {};

    if (!passwordForm.currentPassword) {
      errors.currentPassword = 'Le mot de passe actuel est requis';
    }

    if (!passwordForm.newPassword) {
      errors.newPassword = 'Le nouveau mot de passe est requis';
    } else if (passwordForm.newPassword.length < 8) {
      errors.newPassword = 'Le mot de passe doit contenir au moins 8 caractères';
    }

    if (!passwordForm.confirmPassword) {
      errors.confirmPassword = 'Veuillez confirmer le mot de passe';
    } else if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      errors.confirmPassword = 'Les mots de passe ne correspondent pas';
    }

    return errors;
  };

  // Handle profile update
  const handleProfileSubmit = async (e) => {
    e.preventDefault();

    const errors = validateProfileForm();
    if (Object.keys(errors).length > 0) {
      setProfileErrors(errors);
      return;
    }

    setProfileLoading(true);
    setProfileErrors({});

    try {
      // Call API to update profile
      const result = await api.updateProfile(profileForm);

      if (result.success) {
        // Update user in context
        updateUser(result.data);

        toast.success('Profil mis à jour avec succès', {
          position: 'top-right',
          autoClose: 3000,
        });
      }
    } catch (error) {
      console.error('Profile update error:', error);
      toast.error(error.message || 'Erreur lors de la mise à jour du profil', {
        position: 'top-right',
        autoClose: 3000,
      });
    } finally {
      setProfileLoading(false);
    }
  };

  // Handle password update
  const handlePasswordSubmit = async (e) => {
    e.preventDefault();

    const errors = validatePasswordForm();
    if (Object.keys(errors).length > 0) {
      setPasswordErrors(errors);
      return;
    }

    setPasswordLoading(true);
    setPasswordErrors({});

    try {
      // Call API to update password
      const result = await api.updatePassword(
        passwordForm.currentPassword,
        passwordForm.newPassword
      );

      if (result.success) {
        toast.success(result.message || 'Mot de passe modifié avec succès', {
          position: 'top-right',
          autoClose: 3000,
        });

        // Reset password form
        setPasswordForm({
          currentPassword: '',
          newPassword: '',
          confirmPassword: '',
        });
      }
    } catch (error) {
      console.error('Password update error:', error);
      toast.error(error.message || 'Erreur lors de la modification du mot de passe', {
        position: 'top-right',
        autoClose: 3000,
      });
      setPasswordErrors({
        currentPassword: error.message,
      });
    } finally {
      setPasswordLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Mon Profil</h1>
          <p className="text-sm text-gray-500 mt-1">
            Gérez vos informations personnelles et votre sécurité
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* User Info Card */}
        <Card className="lg:col-span-1">
          <div className="flex flex-col items-center text-center">
            <div className="w-24 h-24 bg-primary rounded-full flex items-center justify-center mb-4">
              <span className="text-white text-3xl font-bold">
                {getInitials(`${user?.firstname || ''} ${user?.lastname || ''}`)}
              </span>
            </div>
            <h3 className="text-xl font-semibold text-gray-900">
              {user?.firstname} {user?.lastname}
            </h3>
            <p className="text-sm text-gray-500 mt-1">{user?.email}</p>
            <div className="mt-4 px-3 py-1 bg-primary/10 text-primary rounded-full text-xs font-medium">
              {user?.role === 'super_admin' ? 'Super Admin' : user?.role === 'admin' ? 'Admin' : 'Support'}
            </div>

            <div className="w-full mt-6 pt-6 border-t border-gray-200">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-500">Compte actif</span>
                <CheckCircleIcon className="w-5 h-5 text-success" />
              </div>
            </div>
          </div>
        </Card>

        {/* Forms Card */}
        <Card className="lg:col-span-2">
          {/* Tabs */}
          <div className="flex border-b border-gray-200 mb-6">
            <button
              onClick={() => setActiveTab('info')}
              className={`px-4 py-2 -mb-px border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'info'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Informations personnelles
            </button>
            <button
              onClick={() => setActiveTab('password')}
              className={`px-4 py-2 -mb-px border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'password'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Sécurité
            </button>
          </div>

          {/* Profile Info Tab */}
          {activeTab === 'info' && (
            <form onSubmit={handleProfileSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Prénom"
                  type="text"
                  name="firstname"
                  value={profileForm.firstname}
                  onChange={handleProfileChange}
                  error={profileErrors.firstname}
                  placeholder="Prénom"
                  leftIcon={<UserCircleIcon className="w-5 h-5" />}
                  required
                />

                <Input
                  label="Nom"
                  type="text"
                  name="lastname"
                  value={profileForm.lastname}
                  onChange={handleProfileChange}
                  error={profileErrors.lastname}
                  placeholder="Nom"
                  leftIcon={<UserCircleIcon className="w-5 h-5" />}
                  required
                />
              </div>

              <Input
                label="Email"
                type="email"
                name="email"
                value={profileForm.email}
                onChange={handleProfileChange}
                error={profileErrors.email}
                placeholder="exemple@email.com"
                leftIcon={<EnvelopeIcon className="w-5 h-5" />}
                required
              />

              <Input
                label="Téléphone"
                type="tel"
                name="phone"
                value={profileForm.phone}
                onChange={handleProfileChange}
                error={profileErrors.phone}
                placeholder="+33 6 12 34 56 78"
                leftIcon={<PhoneIcon className="w-5 h-5" />}
              />

              <div className="flex justify-end pt-4">
                <Button
                  type="submit"
                  variant="primary"
                  loading={profileLoading}
                  disabled={profileLoading}
                >
                  Enregistrer les modifications
                </Button>
              </div>
            </form>
          )}

          {/* Password Tab */}
          {activeTab === 'password' && (
            <form onSubmit={handlePasswordSubmit} className="space-y-4">
              <Input
                label="Mot de passe actuel"
                type="password"
                name="currentPassword"
                value={passwordForm.currentPassword}
                onChange={handlePasswordChange}
                error={passwordErrors.currentPassword}
                placeholder="••••••••"
                leftIcon={<LockClosedIcon className="w-5 h-5" />}
                required
              />

              <Input
                label="Nouveau mot de passe"
                type="password"
                name="newPassword"
                value={passwordForm.newPassword}
                onChange={handlePasswordChange}
                error={passwordErrors.newPassword}
                placeholder="••••••••"
                leftIcon={<LockClosedIcon className="w-5 h-5" />}
                required
              />

              <Input
                label="Confirmer le mot de passe"
                type="password"
                name="confirmPassword"
                value={passwordForm.confirmPassword}
                onChange={handlePasswordChange}
                error={passwordErrors.confirmPassword}
                placeholder="••••••••"
                leftIcon={<LockClosedIcon className="w-5 h-5" />}
                required
              />

              <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
                <p className="text-sm text-gray-700">
                  <strong>Conseils de sécurité :</strong>
                </p>
                <ul className="mt-2 text-sm text-gray-600 space-y-1 list-disc list-inside">
                  <li>Minimum 8 caractères</li>
                  <li>Utilisez des lettres majuscules et minuscules</li>
                  <li>Incluez des chiffres et des caractères spéciaux</li>
                  <li>Évitez les mots de passe courants</li>
                </ul>
              </div>

              <div className="flex justify-end pt-4">
                <Button
                  type="submit"
                  variant="primary"
                  loading={passwordLoading}
                  disabled={passwordLoading}
                >
                  Modifier le mot de passe
                </Button>
              </div>
            </form>
          )}
        </Card>
      </div>
    </div>
  );
};

export default Profile;
